import { ReactNode, useCallback, useMemo, createContext, useContext, useEffect, useRef } from 'react'

import { setCurrentBugsnagUser } from '@legacy/initializers/bugsnagHelpers'
import WhoAmI from '@legacy/models/WhoAmI'
import * as routePaths from '@legacy/routePaths'
import { signInPath, resetPasswordPath, acceptInvitationPath, setPasswordPath } from '@legacy/routePaths'
import { subSeconds } from 'date-fns'
import { useLocation, useNavigate, useRouteLoaderData } from 'react-router'

import { refresh } from '@shared/api/auth'
import { authLoader } from '@shared/utils/authLoader'
import { AUTH_COOKIE_NAME, CookieManager } from '@shared/utils/CookieManager'

const EXPIRY_REFRESH_THRESHOLD_SECONDS = 900 // 15 minutes

export type DecodedJwt = {
  iat: number
  exp: number
  gid: string
}

export type ContextType = {
  getToken: (refreshLastUserActiveTime?: boolean) => string | null | undefined
  whoAmI: WhoAmI | null | undefined
  resetAuthentication: () => void
  onAuthenticated: (whoAmI: WhoAmI, token: string | null | undefined) => void
  disabled?: boolean
}

const AuthenticationContext = createContext<ContextType | undefined>(undefined)

const refreshToken = async (token: string) => {
  const apiResponse = await refresh(token)

  if (!apiResponse.ok) return null

  const authHeader = apiResponse.headers.get('Authorization')

  const newToken = authHeader ? authHeader.replace('Bearer ', '') : null
  return newToken
}

const useAuthentication = () => {
  const context = useContext(AuthenticationContext)
  if (!context) {
    throw new Error(`useAuthentication must be used within an AuthenticationProvider`)
  }
  return context
}

interface ProviderProps {
  children: ReactNode
  disable?: boolean
}

const AuthenticationProvider = ({ children, disable }: ProviderProps) => {
  const { whoAmI } = useRouteLoaderData<typeof authLoader>('protected-layout') ?? {}
  const navigate = useNavigate()
  const cookieManager = useMemo(() => new CookieManager(AUTH_COOKIE_NAME), [])
  const lastUserActiveTime = useRef<number>(0)
  const location = useLocation()

  const onAuthenticated = (whoAmI: WhoAmI, token: string | null | undefined) => {
    setCurrentBugsnagUser(whoAmI)
    if (token) cookieManager.setCookie(token)
  }

  const getToken = (refreshLastUserActiveTime = true) => {
    const cookie = cookieManager.getCookie()

    if (refreshLastUserActiveTime) {
      lastUserActiveTime.current = Date.now()
    }

    if (cookie.status === 'success') return cookie.value

    return null
  }

  const resetAuthentication = useCallback(() => {
    setCurrentBugsnagUser(undefined)
    cookieManager.deleteCookie()
  }, [cookieManager])

  const value: ContextType = {
    getToken,
    whoAmI: whoAmI as unknown as WhoAmI,
    resetAuthentication,
    onAuthenticated,
    disabled: disable,
  }

  const isUnauthenticatedPath = [signInPath(), resetPasswordPath(), acceptInvitationPath(), setPasswordPath()].includes(
    location.pathname,
  )

  /**
   * The lastUserActiveTime stores the last time the getToken function was called. This function is only called by
   * API functions so this lets us determine that the user is actively using the application. The caveat here is that any
   * page with polling will always appear as if the user is active.
   *
   * If the cookie and the lastUserActiveTime are both greater than the expiry threshold, then this
   * useEffect will refresh the token.
   *
   * If the cookie expired, then automatically navigate the user to the login screen.
   */
  useEffect(() => {
    if (disable || isUnauthenticatedPath) return
    const interval = setInterval(async () => {
      const cookie = cookieManager.getCookie()

      if (cookie.status !== 'success' || !cookie.expiresAt || Date.now() > cookie?.expiresAt) {
        cookieManager.deleteCookie()
        return navigate(routePaths.signInPath())
      }
      if (lastUserActiveTime.current === 0) return

      const expireThreshold = subSeconds(new Date(cookie.expiresAt), EXPIRY_REFRESH_THRESHOLD_SECONDS).getTime()
      if (Date.now() > expireThreshold && lastUserActiveTime.current > expireThreshold) {
        const newToken = await refreshToken(cookie.value)
        if (newToken) cookieManager.setCookie(newToken)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [isUnauthenticatedPath, cookieManager, disable, navigate])

  return <AuthenticationContext.Provider value={value}>{children}</AuthenticationContext.Provider>
}

export { AuthenticationProvider, useAuthentication }
